import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AddonCategory from './AddonCategory';
import PriceTag from './ui/PriceTag';
import './MenuItemModal.css';

const defaultCurrency = { code: 'USD', symbol: '$', position: 'before' };
const MenuItemModal = ({ item, addons = [], onClose, onAddToCart, getImageUrl, currency = defaultCurrency }) => {
  const { t, i18n } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [selectedByCategory, setSelectedByCategory] = useState({}); // addonId -> [{ id, name, price, category }]
  const [selectedSizeId, setSelectedSizeId] = useState(null);

  const sizes = Array.isArray(item?.sizes) ? item.sizes : [];

  // Localized size name with fallback to the English base name.
  const sizeLabelFor = (s) => {
    if (!s) return '';
    const lang = i18n.language || 'en';
    if (lang.startsWith('ar')) return s.nameAr || s.name;
    if (lang.startsWith('es')) return s.nameEs || s.name;
    return s.name;
  };

  // Pre-select default options when addons load
  useEffect(() => {
    const initial = {};
    addons.forEach(cat => {
      const defaults = (cat.options || []).filter(o => o.isDefault);
      if (defaults.length > 0) {
        initial[cat.id] = defaults.map(o => ({
          id: o.id,
          name: o.name,
          price: o.price,
          category: cat.categoryName
        }));
      } else {
        initial[cat.id] = [];
      }
    });
    setSelectedByCategory(initial);
  }, [addons]);

  // Pre-select the default size (or the first) when the item changes.
  useEffect(() => {
    if (!Array.isArray(item?.sizes) || item.sizes.length === 0) {
      setSelectedSizeId(null);
      return;
    }
    const def = item.sizes.find(s => s.isDefault) || item.sizes[0];
    setSelectedSizeId(def ? def.id : null);
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCategorySelect = (addonId, selectedOptions) => {
    setSelectedByCategory(prev => ({ ...prev, [addonId]: selectedOptions }));
  };

  const allSelectedAddons = () => {
    return Object.values(selectedByCategory).flat();
  };

  const addonsTotal = () => {
    return allSelectedAddons().reduce((sum, o) => sum + (Number(o.price) || 0), 0);
  };

  const basePrice = parseFloat(item?.price) || 0;
  const selectedSize = sizes.find(s => s.id === selectedSizeId) || null;
  // A selected size's price replaces the base price; addons add on top.
  const unitBase = selectedSize ? (Number(selectedSize.price) || 0) : basePrice;
  const unitTotal = unitBase + addonsTotal();
  const totalPrice = unitTotal * quantity;

  const requiredSatisfied = () => {
    return addons.filter(cat => cat.isRequired).every(cat => {
      const sel = selectedByCategory[cat.id] || [];
      return sel.length > 0;
    });
  };

  const sizeSatisfied = sizes.length === 0 || !!selectedSize;
  const canAdd = requiredSatisfied() && sizeSatisfied;

  const handleAddToCart = () => {
    if (!canAdd) return;
    // Record the chosen size as a display-only modifier (price 0 — its cost is
    // already folded into unitBase). This rides in selected_addons end-to-end,
    // so the cart, order payload, and KDS/receipt show it with no schema change.
    const selectedAddons = [];
    if (selectedSize) {
      selectedAddons.push({
        category: t('menu.sizeLabel', 'Size'),
        option: sizeLabelFor(selectedSize),
        price: 0
      });
    }
    allSelectedAddons().forEach(o => {
      selectedAddons.push({
        category: o.category,
        option: o.name,
        price: Number(o.price)
      });
    });
    onAddToCart({
      ...item,
      quantity,
      price: unitTotal,
      selectedAddons
    });
    onClose();
  };

  if (!item) return null;

  return (
    <div className="menu-item-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="menu-item-modal-title">
      <div className="menu-item-modal" onClick={e => e.stopPropagation()}>
        <div className="menu-item-modal-header">
          <h2 id="menu-item-modal-title" className="menu-item-modal-title">{item.name}</h2>
          <button type="button" className="menu-item-modal-close" onClick={onClose} aria-label={t('common.close')}>
            ×
          </button>
        </div>

        <div className="menu-item-modal-scroll">
          {item.image_url ? (
            <img src={getImageUrl ? getImageUrl(item.image_url) : item.image_url} alt={item.name} className="menu-item-modal-image" />
          ) : (
            <div className="menu-item-modal-image-placeholder">🍽️</div>
          )}
          <p className="menu-item-modal-price">
            <PriceTag amount={unitBase} currency={currency} size="md" color="inherit" weight="bold" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
          </p>
          {item.description && (
            <p className="menu-item-modal-description">{item.description}</p>
          )}

          {sizes.length > 0 && (
            <div className="menu-item-modal-sizes" role="radiogroup" aria-label={t('menu.chooseSize', 'Choose a size')}>
              <p className="menu-item-modal-sizes-label">{t('menu.chooseSize', 'Choose a size')}</p>
              {sizes.map(s => {
                const isSelected = s.id === selectedSizeId;
                return (
                  <label key={s.id} className={`menu-item-modal-size-row ${isSelected ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name={`size-${item.id}`}
                      className="menu-item-modal-size-input"
                      checked={isSelected}
                      onChange={() => setSelectedSizeId(s.id)}
                    />
                    <span className="menu-item-modal-size-name">{sizeLabelFor(s)}</span>
                    <span className="menu-item-modal-size-price">
                      <PriceTag amount={Number(s.price) || 0} currency={currency} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {addons.length > 0 && (
            <div className="menu-item-modal-addons">
              {addons.map(cat => (
                <AddonCategory
                  key={cat.id}
                  category={cat}
                  selectedOptions={selectedByCategory[cat.id] || []}
                  onSelect={opts => handleCategorySelect(cat.id, opts)}
                  currency={currency}
                />
              ))}
            </div>
          )}
        </div>

        <div className="menu-item-modal-footer">
          <div className="menu-item-modal-quantity">
            <button type="button" className="menu-item-modal-qty-btn" onClick={() => setQuantity(q => Math.max(1, q - 1))} aria-label={t('menu.cart.decreaseQuantity')}>−</button>
            <span className="menu-item-modal-qty-value">{quantity}</span>
            <button type="button" className="menu-item-modal-qty-btn" onClick={() => setQuantity(q => q + 1)} aria-label={t('menu.cart.increaseQuantity')}>+</button>
          </div>
          <button
            type="button"
            className="menu-item-modal-add-btn"
            onClick={handleAddToCart}
            disabled={!canAdd}
          >
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.5em' }}>
              {t('menu.addToCart')}
              <PriceTag amount={totalPrice} currency={currency} size="md" color="inherit" weight="bold" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
            </span>
          </button>
          {!canAdd && addons.some(c => c.isRequired) && (
            <p className="menu-item-modal-validation">Please select an option for all required categories.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuItemModal;
